'use client';

import { PlayIcon } from '@heroicons/react/24/solid';
import { useTranslations } from 'next-intl';

import { HeroIcon, Image, Typography } from '@/components/atoms';
import { HeroBanner, LinkButton } from '@/components/molecules';
import { createImage } from '@/lib/utils/createImageObject';
import { cn } from '@/lib/utils/tailwind';

import type { LinkedContent, LinkedPage } from '@/lib/api/types';

interface ContentBannerProps {
  title?: string;
  subtitle?: string;
  media?: {
    landscape: { path: string; root: string; url: string };
    portrait: { path: string; root: string; url: string };
  };
  content?: LinkedContent | LinkedPage;
  buttonLabel?: string;
}

const ContentBanner = ({
  title,
  subtitle,
  media,
  content,
  buttonLabel,
}: ContentBannerProps) => {
  const t = useTranslations();
  let imageObject;
  if (media?.landscape.url || media?.portrait.url) {
    imageObject = createImage(media);
  } else {
    imageObject = createImage();
  }

  const isPage = (content: LinkedContent | LinkedPage): content is LinkedPage =>
    content.content_type === 'page';

  const generateURL = (content: LinkedContent | LinkedPage) => {
    if (isPage(content)) {
      return content.home_page ? '/' : '/' + content.slug;
    }

    return `/${content.content_type}/${content.id}`;
  };

  const translatedContentType =
    content && content !== undefined
      ? t(`CONTENT_TYPE_${content.content_type.toUpperCase()}`)
      : undefined;

  return (
    <HeroBanner
      as={'header'}
      media={
        <>
          <Image
            src={imageObject.landscape.url}
            alt={title ?? 'default-alt'}
            className="object-cover rounded-none"
            wrapperClassName="rounded-none hidden sm:block"
            priority
          />
          <Image
            src={imageObject.portrait.url}
            alt={title ?? 'default-alt'}
            className="object-cover rounded-none"
            wrapperClassName="rounded-none sm:hidden"
            priority
          />
          <div
            className="absolute inset-0 bg-gradient-hero pointer-events-none"
            aria-hidden
          />
        </>
      }
      className={cn(
        'relative',
        'aspect-[4/5] sm:aspect-video sm:max-h-[85vh] sm:max-w-[1440px]',
        'sm:mx-auto',
        'overflow-hidden',
        'bg-[var(--hero-gradient)]',
      )}
    >
      <div className="[align-items:var(--hero-content-item-alignment)] [text-align:var(--hero-content-text-alignment)] flex flex-col w-auto sm:w-[28.5rem] z-10 px-(--widget-padding-horizontal) py-(--widget-padding-vertical)">
        {content && !isPage(content) && translatedContentType && (
          <div>
            <Typography.Text
              variant={'body'}
              className={cn([
                'hero-text-display',
                'px-2',
                'py-1',
                'w-fit',
                'hero-text-style',
                'bg-(--hero-text-background)',
                'text-shadow-(--hero-text-shadow)',
                'text-(--hero-text-color)',
                'text-(length:--text-body-md)',
                'font-(--font-weight-body-md-bold)',
              ])}
            >
              {translatedContentType}
            </Typography.Text>
          </div>
        )}
        {title && (
          <div>
            <Typography.Heading
              variant="h1"
              className={cn([
                'px-2',
                'leading-none!',
                'hero-text-display',
                'hero-box-decoration',
                'bg-(--hero-text-background)',
                'text-(--hero-text-color)',
                'text-shadow-(--hero-text-shadow)',
                'max-w-[25ch]',
                'text-balance',
                'text-(length:--text-h6)',
                'md:text-(length:--text-h5)',
                'font-(--font-weight-h6)',
              ])}
            >
              {title}
            </Typography.Heading>
          </div>
        )}
        {subtitle && (
          <div>
            <Typography.Text
              variant={'paragraph'}
              className={cn([
                'hero-text-display',
                'hero-box-decoration',
                'hero-text-style',
                'max-w-[25ch]',
                'text-shadow-(--hero-text-shadow)',
                'text-(--hero-text-color)',
                'bg-(--hero-text-background)',
                'py-1 px-2',
                'line-clamp-2!',
                'sm:line-clamp-3!',
                'md:line-clamp-none!',
              ])}
            >
              {subtitle}
            </Typography.Text>
          </div>
        )}
        {buttonLabel && content && (
          <div className="mt-(--horizontal-spacing-6)">
            <LinkButton href={generateURL(content)}>
              <HeroIcon icon={PlayIcon} />
              {buttonLabel}
            </LinkButton>
          </div>
        )}
      </div>
    </HeroBanner>
  );
};

export { ContentBanner };
